import { useState } from 'react';
import type { Customer, NoteEntry } from '../../../../mocks/crm';
import { agencyInfo } from '../../../../mocks/dashboard';

const brand = agencyInfo.brandColor;

const statusColors: Record<string, { bg: string; text: string }> = {
  Confirmed: { bg: 'bg-emerald-50', text: 'text-emerald-700' },
  Pending: { bg: 'bg-amber-50', text: 'text-amber-700' },
  Cancelled: { bg: 'bg-red-50', text: 'text-red-700' },
  Completed: { bg: 'bg-gray-100', text: 'text-gray-600' },
};

const payStatusColors: Record<string, { bg: string; text: string }> = {
  Paid: { bg: 'bg-emerald-50', text: 'text-emerald-700' },
  Partial: { bg: 'bg-amber-50', text: 'text-amber-700' },
  Unpaid: { bg: 'bg-red-50', text: 'text-red-700' },
  Refunded: { bg: 'bg-gray-100', text: 'text-gray-500' },
};

interface CustomerDetailProps {
  customer: Customer;
}

export default function CustomerDetail({ customer }: CustomerDetailProps) {
  const [activeTab, setActiveTab] = useState<'bookings' | 'notes' | 'profile'>('bookings');
  const [notes, setNotes] = useState<NoteEntry[]>(customer.notes);
  const [noteText, setNoteText] = useState('');

  const handleAddNote = () => {
    if (!noteText.trim()) return;
    const newNote: NoteEntry = {
      id: `nc-${Date.now()}`,
      author: 'You',
      authorAvatar: 'MK',
      text: noteText.trim(),
      timestamp: new Date().toISOString().slice(0, 16).replace('T', ' '),
      type: 'note',
    };
    setNotes((prev) => [newNote, ...prev]);
    setNoteText('');
  };

  const tabs = [
    { id: 'bookings' as const, label: 'Bookings', count: customer.bookings.length },
    { id: 'notes' as const, label: 'Notes', count: notes.length },
    { id: 'profile' as const, label: 'Profile' },
  ];

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-white">
      {/* Customer Header */}
      <div className="px-8 py-6 border-b border-gray-100">
        <div className="flex items-start gap-5">
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center text-white text-xl font-bold flex-shrink-0"
            style={{ backgroundColor: brand }}
          >
            {customer.avatar}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="text-xl font-bold text-gray-800">{customer.name}</h2>
              {customer.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-xs font-medium px-2.5 py-1 rounded-full"
                  style={{ backgroundColor: `${brand}15`, color: brand }}
                >
                  {tag}
                </span>
              ))}
            </div>
            <div className="flex items-center gap-4 mt-1.5 flex-wrap">
              <div className="flex items-center gap-1.5 text-gray-500">
                <i className="ri-mail-line text-sm" />
                <span className="text-sm">{customer.email}</span>
              </div>
              <div className="flex items-center gap-1.5 text-gray-500">
                <i className="ri-phone-line text-sm" />
                <span className="text-sm">{customer.phone}</span>
              </div>
              <div className="flex items-center gap-1.5 text-gray-500">
                <i className="ri-map-pin-line text-sm" />
                <span className="text-sm">{customer.nationality}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button className="flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-xl text-white cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap" style={{ backgroundColor: brand }}>
              <i className="ri-calendar-add-line" />
              New Booking
            </button>
            <button className="w-9 h-9 flex items-center justify-center rounded-xl border border-gray-200 text-gray-500 hover:bg-gray-50 cursor-pointer transition-colors">
              <i className="ri-whatsapp-line text-base" />
            </button>
            <button className="w-9 h-9 flex items-center justify-center rounded-xl border border-gray-200 text-gray-500 hover:bg-gray-50 cursor-pointer transition-colors">
              <i className="ri-more-2-fill text-base" />
            </button>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-4 gap-4 mt-5">
          {[
            { label: 'Total Bookings', value: customer.totalBookings, icon: 'ri-suitcase-line' },
            { label: 'Total Spent', value: customer.totalSpent, icon: 'ri-money-dollar-circle-line' },
            { label: 'Member Since', value: customer.joinedAt, icon: 'ri-calendar-check-line' },
            { label: 'Last Booking', value: customer.lastBooking, icon: 'ri-time-line' },
          ].map((s) => (
            <div key={s.label} className="bg-gray-50 rounded-xl p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <i className={`${s.icon} text-xs text-gray-400`} />
                <p className="text-xs text-gray-400">{s.label}</p>
              </div>
              <p className="text-sm font-bold text-gray-800">{s.value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-100 px-8">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className="flex items-center gap-2 px-1 py-3 mr-6 text-sm font-medium cursor-pointer transition-colors whitespace-nowrap"
            style={
              activeTab === tab.id
                ? { color: brand, borderBottom: `2px solid ${brand}` }
                : { color: '#9CA3AF' }
            }
          >
            {tab.label}
            {tab.count !== undefined && (
              <span
                className="text-xs font-bold px-1.5 py-0.5 rounded-full"
                style={
                  activeTab === tab.id
                    ? { backgroundColor: `${brand}20`, color: brand }
                    : { backgroundColor: '#F3F4F6', color: '#9CA3AF' }
                }
              >
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto px-8 py-6">
        {/* Bookings Tab */}
        {activeTab === 'bookings' && (
          <div>
            <div className="overflow-hidden rounded-2xl border border-gray-100">
              <table className="w-full">
                <thead className="bg-gray-50/80">
                  <tr>
                    {['Booking ID', 'Service', 'Travel Date', 'Pax', 'Status', 'Amount', 'Payment', 'Agent'].map((h) => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-50">
                  {customer.bookings.map((bk) => {
                    const sc = statusColors[bk.status];
                    const pc = payStatusColors[bk.payStatus];
                    return (
                      <tr key={bk.id} className="hover:bg-gray-50/60 transition-colors">
                        <td className="px-4 py-3.5">
                          <span className="text-sm font-semibold" style={{ color: brand }}>{bk.id}</span>
                        </td>
                        <td className="px-4 py-3.5">
                          <div className="flex items-center gap-2">
                            <i className={`${bk.serviceIcon} text-sm text-gray-400`} />
                            <span className="text-sm text-gray-700 whitespace-nowrap max-w-[160px] truncate block">{bk.service}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3.5">
                          <p className="text-sm text-gray-600 whitespace-nowrap">{bk.travelDate}</p>
                          <p className="text-xs text-gray-400">{bk.returnDate}</p>
                        </td>
                        <td className="px-4 py-3.5">
                          <span className="text-sm text-gray-600">{bk.pax}</span>
                        </td>
                        <td className="px-4 py-3.5">
                          <span className={`inline-flex text-xs font-semibold px-2.5 py-1 rounded-full ${sc.bg} ${sc.text}`}>
                            {bk.status}
                          </span>
                        </td>
                        <td className="px-4 py-3.5">
                          <span className="text-sm font-bold text-gray-800 whitespace-nowrap">{bk.amount}</span>
                        </td>
                        <td className="px-4 py-3.5">
                          <span className={`inline-flex text-xs font-semibold px-2.5 py-1 rounded-full ${pc.bg} ${pc.text}`}>
                            {bk.payStatus}
                          </span>
                        </td>
                        <td className="px-4 py-3.5">
                          <span className="text-sm text-gray-500 whitespace-nowrap">{bk.agent}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Notes Tab */}
        {activeTab === 'notes' && (
          <div>
            <div className="bg-gray-50 rounded-2xl p-4 mb-5">
              <textarea
                rows={3}
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="Add a note about this customer..."
                className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2.5 resize-none focus:outline-none bg-white placeholder-gray-400"
              />
              <div className="flex justify-end mt-2">
                <button
                  onClick={handleAddNote}
                  disabled={!noteText.trim()}
                  className="text-sm font-semibold px-4 py-2 rounded-xl text-white cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{ backgroundColor: brand }}
                >
                  Save Note
                </button>
              </div>
            </div>
            {notes.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-8">No notes yet.</p>
            ) : (
              <div className="space-y-4">
                {notes.map((note) => (
                  <div key={note.id} className="flex gap-3">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                      style={{ backgroundColor: brand }}
                    >
                      {note.authorAvatar}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-semibold text-gray-700">{note.author}</span>
                        <span className="text-xs text-gray-400">{note.timestamp}</span>
                      </div>
                      <div className="bg-gray-50 rounded-xl px-4 py-3">
                        <p className="text-sm text-gray-600 leading-relaxed">{note.text}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="grid grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <i className="ri-user-line" style={{ color: brand }} />
                Personal Information
              </h3>
              <div className="space-y-3">
                {[
                  { label: 'Date of Birth', value: customer.dob },
                  { label: 'Nationality', value: customer.nationality },
                  { label: 'Address', value: customer.address },
                ].map((f) => (
                  <div key={f.label} className="bg-gray-50 rounded-xl px-4 py-3">
                    <p className="text-xs text-gray-400 mb-0.5">{f.label}</p>
                    <p className="text-sm font-semibold text-gray-700">{f.value}</p>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <i className="ri-passport-line" style={{ color: brand }} />
                Passport Details
              </h3>
              <div className="space-y-3">
                {[
                  { label: 'Passport Number', value: customer.passportNo },
                  { label: 'Expiry Date', value: customer.passportExpiry },
                  { label: 'Issuing Country', value: customer.nationality },
                ].map((f) => (
                  <div key={f.label} className="bg-gray-50 rounded-xl px-4 py-3">
                    <p className="text-xs text-gray-400 mb-0.5">{f.label}</p>
                    <p className="text-sm font-semibold text-gray-700">{f.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
